import React, { useState, useEffect, useMemo } from 'react'
import { Box, CircularProgress, Alert } from '@mui/material'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts'
import { apiService } from '../../services/api'
import MetricCard from '../ui/MetricCard'
import AgentDrillDown from './AgentDrillDown'
import {
  ReportShell, FilterBar, KpiStrip, Section, ReportTable, SubTabs, fmt,
  useDistinctFilters, buildPlkFilterFields,
} from '../report-shell'

const SectionHeader = ({ children }) => (
  <div className="flex items-center gap-2 mb-3">
    <div className="w-1 h-6 bg-plus-500 rounded-full"></div>
    <h2 className="text-lg font-semibold text-gray-800">{children}</h2>
  </div>
)

const PlusSales = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [monthlyView, setMonthlyView] = useState('chart')
  const [filters, setFilters] = useState({})

  const fetchData = async (currentFilters = filters) => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.getPlusSales(currentFilters)
      setData(response.data)
    } catch (err) {
      setError(`Błąd pobierania danych: ${err.message}`)
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onFiltersChange = (next) => {
    setFilters(next)
    fetchData(next)
  }

  // Daily sales chart data
  const dailyChartData = useMemo(() => {
    if (!data?.daily_sales) return []
    return data.daily_sales.map(d => ({
      ...d,
      label: d.date.slice(5), // MM-DD
    }))
  }, [data])

  // Salon sales table columns
  const salonColumns = [
    { field: 'sfid',         header: 'Id SF',        width: '100px', numeric: true },
    { field: 'miasto',       header: 'Miasto',       width: '140px' },
    { field: 'mikrorynek',   header: 'Mikrorynek',   width: '150px' },
    { field: 'region',       header: 'Region',       width: '130px' },
    { field: 'pos_type',     header: 'Typ',          width: '100px' },
    { field: 'agent',        header: 'Agent (firma)', width: '1.4fr', minWidth: 200 },
    { field: 'agent_person', header: 'Osoba prow.',  width: '160px' },
    { field: 'rks',          header: 'RKS',          width: '160px' },
    { field: 'kr',           header: 'KR',           width: '140px' },
    { field: 'kam_code',     header: 'KAM',          width: '110px' },
    { field: 'count',        header: 'Sprzedaż',     width: '110px', sales: true },
  ]

  const salonRows = useMemo(() => {
    if (!data?.salon_sales_last_day) return []
    return data.salon_sales_last_day.map((s, i) => ({ id: s.sfid || i, ...s }))
  }, [data])

  // Monthly sales columns
  const monthlyColumns = [
    { field: 'month', header: 'Miesiąc',  width: '160px' },
    { field: 'count', header: 'Sprzedaż', width: '120px', sales: true },
  ]

  const monthlyRows = useMemo(() => {
    if (!data?.monthly_sales) return []
    return data.monthly_sales.map((m, i) => ({ id: i, ...m }))
  }, [data])

  // Monthly chart data — sorted chronologically, short labels
  const monthlyChartData = useMemo(() => {
    if (!data?.monthly_sales) return []
    return [...data.monthly_sales]
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(m => ({
        ...m,
        label: m.month.slice(2).replace('-', '/'), // "2026-01" → "26/01"
      }))
  }, [data])

  const kpis = data?.kpis || {}
  const ml = data?.month_labels

  const distinct = useDistinctFilters('plk')
  const filterFields = buildPlkFilterFields(distinct, filters, onFiltersChange)

  const kpiTiles = data ? [
    { key: 'goal',        label: 'Cel',          value: fmt.int(kpis.goal || 0),        unit: 'szt.' },
    { key: 'realization', label: 'Realizacja',   value: fmt.int(kpis.realization || 0), unit: 'szt.', sub: `${kpis.realization_pct}% celu` },
    { key: 'estimate',    label: 'Estymata',     value: fmt.int(kpis.estimate || 0),    unit: 'szt.', sub: `${kpis.estimate_pct}% celu` },
    { key: 'avg',         label: 'Śr. dzienna',  value: kpis.avg_daily ?? '—',          unit: 'szt./dzień' },
    { key: 'trend',       label: 'Trend dzienny', value: kpis.trend_daily ?? '—',       unit: 'szt./dzień', sub: 'do celu' },
  ] : []

  return (
    <ReportShell
      title="Plus — Raport sprzedaży"
      subtitle={data ? `Okres: ${kpis.period || ml?.current || 'bieżący miesiąc'}` : 'Sprzedaż OnePlus / Plus Router — agregacje i drill-down per agent.'}
      breadcrumb={['KAM-SFA', 'Plus', 'Raport sprzedaży']}
      accentColor="#00a651"
      filterBar={<FilterBar filters={filterFields} onReset={() => onFiltersChange({})} />}
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
      {/* Row 2: Monthly totals */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <MetricCard title={ml?.current || 'Bieżący mies.'} value={kpis.current_month_sales} />
        <MetricCard title={ml?.prev || 'Poprzedni mies.'}  value={kpis.prev_month_sales} />
        <MetricCard title={ml?.prev2 || '2 mies. temu'}    value={kpis.prev2_month_sales} />
        <MetricCard title="Łącznie"                        value={kpis.total_sales} />
      </div>

      {/* KAM Goals — card grid with progress bars */}
      {data.kam_goals?.length > 0 && (
        <>
          <SectionHeader>Realizacja celów per KAM</SectionHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {data.kam_goals.map((kam, i) => {
              const realPct = kam.realization_pct
              const estPct = kam.estimate_pct
              const realColor = realPct >= 100 ? '#10B981' : realPct >= 80 ? '#F59E0B' : '#EF4444'
              const estColor = estPct >= 100 ? '#10B981' : estPct >= 80 ? '#F59E0B' : '#EF4444'

              return (
                <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                  {/* Card header */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-gray-800">{kam.kam}</span>
                    <span className="text-sm text-gray-500">Cel: {kam.goal} szt</span>
                  </div>

                  {/* Realization */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Realizacja</span>
                      <span className="font-medium">{kam.realization} szt ({realPct}%)</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(realPct, 100)}%`, backgroundColor: realColor }}
                      />
                    </div>
                  </div>

                  {/* Estimate */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Estymata</span>
                      <span className="font-medium">{kam.estimate} szt ({estPct}%)</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(estPct, 100)}%`, backgroundColor: estColor }}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Daily Sales Chart */}
      {dailyChartData.length > 0 && (
        <div className="card mb-6">
          <SectionHeader>Sprzedaż dzień po dniu</SectionHeader>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" fontSize={10} />
                <YAxis />
                <RechartsTooltip
                  labelFormatter={(_, payload) => {
                    if (!payload?.[0]?.payload?.date) return ''
                    return payload[0].payload.date
                  }}
                />
                <Bar dataKey="count" name="Sprzedaż" fill="#4caf50" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </div>
      )}

      {/* Per-Agent drill-down (cards) */}
      {data.agent_sales && data.agent_sales.length > 0 && (
        <div className="card mb-6">
          <SectionHeader>Sprzedaż per Agent (rozwiń, by zobaczyć salony)</SectionHeader>
          {data.agent_sales.slice(0, 30).map((a) => (
            <AgentDrillDown
              key={`${a.agent}|${a.rks}`}
              agent={a.agent || '(brak agenta)'}
              operator="plus"
              summary={{
                rks: a.rks,
                salon_count: a.salon_count,
                sales: a.sales,
                pos_type: a.pos_type,
              }}
              filters={{
                agent: a.agent || undefined,
                rks: filters.rks || undefined,
                pos_type: filters.pos_type || undefined,
                region: filters.region || undefined,
                kr: filters.kr || undefined,
              }}
            />
          ))}
        </div>
      )}

      {/* Per-Salon Sales (last day) */}
      {salonRows.length > 0 && (
        <Section title="Sprzedaż per salon (wczoraj)">
          <ReportTable
            rows={salonRows}
            columns={salonColumns}
            initialSort={{ field: 'count', dir: 'desc' }}
            pageSize={25}
            pageSizeOptions={[25, 50]}
            density="compact"
          />
        </Section>
      )}

      {/* Monthly Sales — chart/table toggle */}
      {monthlyRows.length > 0 && (
        <Section
          title="Historia miesięczna"
          right={
            <SubTabs
              tabs={[{ value: 'chart', label: 'Wykres' }, { value: 'table', label: 'Tabela' }]}
              value={monthlyView}
              onChange={setMonthlyView}
            />
          }
          padded={monthlyView === 'chart'}
        >
          {monthlyView === 'chart' ? (
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" fontSize={11} />
                  <YAxis />
                  <RechartsTooltip
                    labelFormatter={(_, payload) => {
                      if (!payload?.[0]?.payload?.month) return ''
                      return payload[0].payload.month
                    }}
                  />
                  <Bar dataKey="count" name="Sprzedaż" fill="#00A651" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <ReportTable
              rows={monthlyRows}
              columns={monthlyColumns}
              initialSort={{ field: 'month', dir: 'desc' }}
              pageSize={10}
              pageSizeOptions={[10, 25]}
              density="compact"
            />
          )}
        </Section>
      )}
        </>
      )}
    </ReportShell>
  )
}

export default PlusSales
